import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../axios';
import { Organization, User } from '../../types';
import { ApiResponse, ApiResponseData } from '../../types/apiResponse.types';
import { RootState } from '../store';
import { GetCustomTokenRequestBody, UserSignUpFlow } from './auth.types';

export const getCustomToken = createAsyncThunk<string, string>(
  'auth/getCustomToken',
  async (authCode, { fulfillWithValue, rejectWithValue, signal }) => {
    try {
      const response = await axios.post<
        ApiResponseData<{ customToken: string }>,
        ApiResponse<{ customToken: string }>,
        GetCustomTokenRequestBody
      >('/auth-code', { authCode }, { signal });

      return fulfillWithValue(response.data.payload.customToken);
    } catch (e) {
      return rejectWithValue(null);
    }
  }
);

export const getIdentity = createAsyncThunk<{
  user: User;
  org: Organization;
  userSignUpFlow: UserSignUpFlow;
}>(
  'auth/getIdentity',
  async (_, { fulfillWithValue, rejectWithValue, signal }) => {
    try {
      const response = await axios.get<
        ApiResponseData<{
          user: User;
          org: Organization;
          state: UserSignUpFlow;
        }>
      >(`/api/v1/identity`, {
        signal,
      });

      const { user, org, state } = response.data.payload;

      return fulfillWithValue({ user, org, userSignUpFlow: state });
    } catch (error) {
      return rejectWithValue(null);
    }
  }
);

export const signOut = createAsyncThunk<null>(
  'auth/signOut',
  async (_, { fulfillWithValue, rejectWithValue, signal }) => {
    try {
      await axios.get('/logout', { signal });
      return fulfillWithValue(null);
    } catch (error) {
      return rejectWithValue(null);
    }
  }
);

export const sendSupportRequest = createAsyncThunk<null>(
  'auth/sendSupportRequest',
  async (_, { getState, fulfillWithValue, rejectWithValue, signal }) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      await axios.post(
        `services/orgs/${user?.orgId}/users/${user?.userId}/support`,
        undefined,
        { signal }
      );

      return fulfillWithValue(null);
    } catch (e) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);
